import { offsetOf, pointAt } from "./richTextSelection";
import { MergeAttributeClass } from "./sanitiseEmailHtml";

/**
 * Word-level formatting for the rich text editor, done on the selected text with the
 * Selection and Range APIs. The editor offers only what `sanitiseEmailHtml` keeps.
 */
export interface InlineFormat {
  matches: (element: Element) => boolean;
  create: () => HTMLElement;
}

const tagFormat = (names: string[], create: string): InlineFormat => ({
  matches: (element) => names.includes(element.nodeName),
  create: () => document.createElement(create),
});

export const Bold = tagFormat(["STRONG", "B"], "strong");
export const Italic = tagFormat(["EM", "I"], "em");

export const Underline: InlineFormat = {
  matches: (element) =>
    element.nodeName === "U" ||
    (element.nodeName === "SPAN" && /underline/.test((element as HTMLElement).style.textDecoration)),
  create: () => document.createElement("u"),
};

export const Strikethrough: InlineFormat = {
  matches: (element) =>
    element.nodeName === "S" ||
    element.nodeName === "STRIKE" ||
    (element.nodeName === "SPAN" && /line-through/.test((element as HTMLElement).style.textDecoration)),
  create: () => document.createElement("s"),
};

const Chip = `span.${MergeAttributeClass}`;

const hasStyle = (property: string) => (element: Element) =>
  element.nodeName === "SPAN" &&
  !element.classList.contains(MergeAttributeClass) &&
  Boolean((element as HTMLElement).style.getPropertyValue(property));

const isStyledSpan = (element: HTMLElement) =>
  element.nodeName === "SPAN" && !element.classList.contains(MergeAttributeClass) && element.style.length > 0;

const spanWith = (styles: [string, string][]) => {
  const span = document.createElement("span");

  styles.forEach(([property, value]) => span.style.setProperty(property, value));
  return span;
};

const stylesExcept = (element: HTMLElement, property: string) =>
  Array.from(element.style)
    .filter((name) => name !== property)
    .map((name): [string, string] => [name, element.style.getPropertyValue(name)]);

export function currentRange(root: HTMLElement) {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);

  return root.contains(range.commonAncestorContainer) ? range : null;
}

function ancestorMatching(node: Node, root: HTMLElement, matches: InlineFormat["matches"]) {
  for (let element = node.parentElement; element && element !== root; element = element.parentElement) {
    if (matches(element)) {
      return element;
    }
  }

  return null;
}

/** The text in the range, split at its ends so each node is wholly inside it. A merge attribute counts as one piece. */
function selectedTextNodes(root: HTMLElement, range: Range) {
  const { startContainer, startOffset, endContainer, endOffset } = range;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const found: Text[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (range.intersectsNode(node)) {
      found.push(node as Text);
    }
  }

  const selected: Node[] = [];

  found.forEach((original) => {
    const chip = original.parentElement?.closest(Chip);

    if (chip && root.contains(chip)) {
      if (!selected.includes(chip)) {
        selected.push(chip);
      }

      return;
    }

    let node = original;
    const start = original === startContainer ? startOffset : 0;
    const end = original === endContainer ? endOffset : original.length;

    if (start >= end) {
      return;
    }

    if (end < node.length) {
      node.splitText(end);
    }

    if (start > 0) {
      node = node.splitText(start);
    }

    selected.push(node);
  });

  return selected;
}

/** Takes `node` out of `ancestor`, leaving what came before and after still inside it. */
function unwrapAround(node: Node, ancestor: Element) {
  const before = document.createRange();
  const after = document.createRange();

  before.setStart(ancestor, 0);
  before.setEndBefore(node);
  after.setStartAfter(node);
  after.setEnd(ancestor, ancestor.childNodes.length);

  const afterContents = after.extractContents();
  const beforeContents = before.extractContents();
  const parent = ancestor.parentNode as Node;
  const holding = (contents: DocumentFragment) => {
    if (!contents.textContent) {
      return null;
    }

    const element = ancestor.cloneNode(false);

    element.appendChild(contents);
    return element;
  };
  const first = holding(beforeContents);
  const last = holding(afterContents);

  if (first) {
    parent.insertBefore(first, ancestor);
  }

  if (last) {
    parent.insertBefore(last, ancestor.nextSibling);
  }

  while (ancestor.firstChild) {
    parent.insertBefore(ancestor.firstChild, ancestor);
  }

  parent.removeChild(ancestor);
}

function wrap(node: Node, element: HTMLElement) {
  node.parentNode?.insertBefore(element, node);
  element.appendChild(node);
}

const FormatElements = "strong,b,em,i,u,s,strike,span";

/** Joins neighbouring identical elements and drops empty ones, so a run is one element. */
function tidy(root: HTMLElement) {
  let changed = true;

  while (changed) {
    changed = false;

    for (const element of Array.from(root.querySelectorAll(FormatElements))) {
      if (changed || !element.parentNode) {
        continue;
      }

      if (!element.textContent && !element.querySelector("br,span.crmattribute")) {
        element.remove();
        changed = true;
        continue;
      }

      const next = element.nextSibling as Element | null;

      if (
        next?.nodeType === 1 &&
        next.nodeName === element.nodeName &&
        next.getAttribute("style") === element.getAttribute("style") &&
        next.getAttribute("class") === element.getAttribute("class") &&
        element.getAttribute("class") !== "crmattribute"
      ) {
        while (next.firstChild) {
          element.appendChild(next.firstChild);
        }

        next.remove();
        changed = true;
      }
    }
  }
}

function reselect(nodes: Node[]) {
  const first = nodes[0];
  const last = nodes[nodes.length - 1];

  if (!first?.parentNode || !last?.parentNode) {
    return;
  }

  const range = document.createRange();
  const selection = window.getSelection();

  range.setStartBefore(first);
  range.setEndAfter(last);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

const WordCharacter = /[A-Za-z0-9_'’À-ɏ]/;

/** The word a caret is in or at the end of, when it sits in one piece of text. */
function wordAround(root: HTMLElement, range: Range) {
  const node = range.startContainer;

  if (node.nodeType !== 3 || node.parentElement?.closest(Chip)) {
    return null;
  }

  const text = (node as Text).data;
  let start = range.startOffset;
  let end = start;

  if (!WordCharacter.test(text.charAt(start - 1)) && !WordCharacter.test(text.charAt(start))) {
    return null;
  }

  while (start > 0 && WordCharacter.test(text.charAt(start - 1))) {
    start -= 1;
  }

  while (end < text.length && WordCharacter.test(text.charAt(end))) {
    end += 1;
  }

  const word = document.createRange();

  word.setStart(node, start);
  word.setEnd(node, end);
  return root.contains(node) ? word : null;
}

/** What to format: the selection, or the word at a caret, which then gets its caret back. */
function formatTarget(root: HTMLElement) {
  const range = currentRange(root);

  if (!range) {
    return null;
  }

  if (!range.collapsed) {
    return { range, caret: null };
  }

  const word = wordAround(root, range);

  return word ? { range: word, caret: offsetOf(root, range.startContainer, range.startOffset) } : null;
}

function finish(root: HTMLElement, nodes: Node[], caret: number | null) {
  tidy(root);

  if (caret === null) {
    reselect(nodes);
    return;
  }

  const [node, offset] = pointAt(root, caret);
  const range = document.createRange();

  range.setStart(node, offset);
  range.collapse(true);
  window.getSelection()?.removeAllRanges();
  window.getSelection()?.addRange(range);
}

/**
 * Applies the format to the selected words, or takes it off when every one has it. With just
 * a caret in a word it is that word. False when there is nothing to format.
 */
export function toggleFormat(root: HTMLElement, format: InlineFormat) {
  const target = formatTarget(root);
  const nodes = target ? selectedTextNodes(root, target.range) : [];

  if (!target || nodes.length === 0) {
    return false;
  }

  const allHave = nodes.every((node) => ancestorMatching(node, root, format.matches));

  nodes.forEach((node) => {
    if (allHave) {
      for (let ancestor = ancestorMatching(node, root, format.matches); ancestor; ) {
        unwrapAround(node, ancestor);
        ancestor = ancestorMatching(node, root, format.matches);
      }
    } else if (!ancestorMatching(node, root, format.matches)) {
      wrap(node, format.create());
    }
  });

  finish(root, nodes, target.caret);
  return true;
}

/**
 * Sets one style on the selected words, or with no value takes it off, leaving any other
 * style those words carry. With just a caret in a word it is that word. False when there is nothing to style.
 */
export function setSpanStyle(root: HTMLElement, property: string, value: string | null) {
  const target = formatTarget(root);
  const nodes = target ? selectedTextNodes(root, target.range) : [];

  if (!target || nodes.length === 0) {
    return false;
  }

  const has = hasStyle(property);

  nodes.forEach((node) => {
    for (let ancestor = ancestorMatching(node, root, has); ancestor; ancestor = ancestorMatching(node, root, has)) {
      const others = stylesExcept(ancestor as HTMLElement, property);

      unwrapAround(node, ancestor);

      if (others.length > 0) {
        wrap(node, spanWith(others));
      }
    }

    const parent = node.parentElement;

    if (!value) {
      return;
    }

    // Words alone in a plain span take the style there, rather than in a span of their own.
    if (parent && parent !== root && parent.childNodes.length === 1 && isStyledSpan(parent)) {
      parent.style.setProperty(property, value);
    } else {
      wrap(node, spanWith([[property, value]]));
    }
  });

  finish(root, nodes, target.caret);
  return true;
}

export const setColour = (root: HTMLElement, colour: string | null) => setSpanStyle(root, "color", colour);

export const setBackground = (root: HTMLElement, colour: string | null) =>
  setSpanStyle(root, "background-color", colour);

export const setFontSize = (root: HTMLElement, size: string | null) => setSpanStyle(root, "font-size", size);

export const setFontFamily = (root: HTMLElement, family: string | null) =>
  setSpanStyle(root, "font-family", family);

/** The value of a style on the words at the start of the selection, or empty when it has none. */
export function styleAtSelection(root: HTMLElement, property: string) {
  const range = currentRange(root);
  const span = range && ancestorMatching(range.startContainer, root, hasStyle(property));

  return span ? (span as HTMLElement).style.getPropertyValue(property) : "";
}

/** Whether the words at the start of the selection, or the caret, have the format. */
export function isFormatActive(root: HTMLElement, format: InlineFormat) {
  const range = currentRange(root);

  return Boolean(range && ancestorMatching(range.startContainer, root, format.matches));
}
